import { gsap } from 'gsap/all'
import { breakpoint } from 'helpers'
import { debounce, throttle } from 'throttle-debounce'

const el = {
  header: document.querySelector<HTMLElement>('.header'),
  mobileContainer: document.querySelector<HTMLElement>('.header__mobile-container'),
  mobileNavigation: {
    items: document.querySelectorAll<HTMLElement>(
      '.header__mobile-navigation > .header__mobile-navigation-list > .header__mobile-navigation-item, .header__mobile-navigation-my-pages'
    ),
    trigger: document.querySelector<HTMLElement>('.header__mobile-navigation-trigger'),
    mainList: document.querySelector('.header__mobile-navigation > .header__mobile-navigation-list'),
    backTriggers: document.querySelectorAll(
      '.header__mobile-navigation-link--back'
    ) as NodeListOf<HTMLElement>,
    levelTriggers: document.querySelectorAll(
      '.header__mobile-navigation .header__mobile-navigation-item--has-subnavigation > a'
    ) as NodeListOf<HTMLElement>,
    bottomButtons: document.querySelectorAll('.header__mobile-navigation-my-pages'),
  },
}

const isMobileHeaderAlwaysVisible = true
let isMobileNavigationOpen = false
let lastScrollTop = 0

export const header = {
  init: () => {
    if (!el.header || !el.mobileNavigation.trigger) return

    el.mobileNavigation.trigger.addEventListener('click', header.toggleMobileNavigation)

    if (!isMobileHeaderAlwaysVisible) {
      window.addEventListener('resize', debounce(120, header.toggleHeader))
      window.addEventListener('scroll', throttle(60, header.toggleHeader))
    }

    header.handleMobileNavigationLevels()
  },

  toggleHeader: () => {
    const currentScrollTop = -document.body.getBoundingClientRect().top
    const isScrollingDown = currentScrollTop > lastScrollTop
    const isHeaderHidden = currentScrollTop > 21 && isScrollingDown
    const isDesktop = window.innerWidth >= breakpoint.desktop.xs

    gsap.killTweensOf(el.header)

    if (!isMobileNavigationOpen && !isDesktop) {
      gsap.to(el.header, {
        clearProps: 'all',
        duration: 0.567,
        ease: 'power4.inOut',
        overwrite: true,
        y: isHeaderHidden ? '-102%' : 0,
        onComplete: () => {
          if (isHeaderHidden) {
            el.header.classList.add('header--hidden')
          } else {
            el.header.classList.remove('header--hidden')
          }
        },
      })
    }

    if (isDesktop) {
      el.header.classList.remove('header--hidden')
    }

    lastScrollTop = currentScrollTop
  },

  toggleMobileNavigation: () => {
    gsap.to(el.mobileContainer, {
      clearProps: 'all',
      duration: 0.567,
      ease: 'power4.inOut',
      height: isMobileNavigationOpen ? 84 : '100vh',
      overwrite: true,
      onStart: () => {
        isMobileNavigationOpen = !isMobileNavigationOpen

        if (isMobileNavigationOpen) {
          gsap.fromTo(
            el.mobileNavigation.items,
            {
              opacity: 0,
              y: 32,
            },
            {
              clearProps: 'all',
              delay: 0.345,
              duration: 1.23,
              ease: 'power4.out',
              opacity: 1,
              overwrite: true,
              stagger: 0.12,
              y: 0,
            }
          )
        }

        const elHrs = el.mobileNavigation.trigger.querySelectorAll('hr')
        const elHr1 = el.mobileNavigation.trigger.querySelector('hr:nth-of-type(1)')
        const elHr2 = el.mobileNavigation.trigger.querySelector('hr:nth-of-type(2)')
        const elHr3 = el.mobileNavigation.trigger.querySelector('hr:nth-of-type(3)')

        if (isMobileNavigationOpen) {
          gsap
            .timeline()
            .set(elHrs, { clearProps: 'all', overwrite: true }, 0)
            .to(elHr1, { duration: 0.2, ease: 'power3.out', overwrite: true, y: 7 }, 0)
            .to(elHr3, { duration: 0.2, ease: 'power3.out', overwrite: true, y: -7 }, 0)
            .set(elHr2, { overwrite: true, scale: 0 }, 0.2)
            .to(elHr1, { duration: 1, ease: 'elastic.out', rotation: -45 }, 0.2)
            .to(elHr3, { duration: 1, ease: 'elastic.out', rotation: 45 }, 0.2)
        } else {
          gsap
            .timeline()
            .set(elHr1, { overwrite: true, rotation: -45, y: 6 }, 0)
            .set(elHr2, { overwrite: true, scale: 0 }, 0)
            .set(elHr3, { overwrite: true, rotation: 45, y: -6 }, 0)
            .to(elHr1, { duration: 0.2, ease: 'power3.out', rotation: 0 }, 0)
            .to(elHr3, { duration: 0.2, ease: 'power3.out', rotation: 0 }, 0)
            .set(elHr2, { clearProps: 'all' }, 0.2)
            .to(elHr1, { clearProps: 'all', duration: 1, ease: 'elastic.out', y: 0 }, 0.2)
            .to(elHr3, { clearProps: 'all', duration: 1, ease: 'elastic.out', y: 0 }, 0.2)
        }
      },
      onComplete: () => {
        if (isMobileNavigationOpen) {
          el.mobileContainer.classList.add('header__mobile-container--open')
        } else {
          el.mobileContainer.classList.remove('header__mobile-container--open')
        }
      },
    })
  },

  handleMobileNavigationLevels: () => {
    el.mobileNavigation.levelTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        if (window.innerWidth < breakpoint.desktop.xs) {
          const currentItems = Array.from(trigger.closest('.header__mobile-navigation-list').children)
          const activeList = trigger.nextElementSibling
          const activeCurrentItem = trigger.parentElement
          const activeItems = Array.from(trigger.nextElementSibling.children)
          const initialButtonsPosition = el.mobileNavigation.bottomButtons[0].getBoundingClientRect().top

          gsap.to(currentItems, {
            duration: 0.2,
            ease: 'power2.out',
            opacity: 0,
            overwrite: true,
            stagger: 0.05,
            y: -33,
            onComplete: () => {
              activeList.classList.add('header__mobile-navigation-list--active')
              activeCurrentItem.classList.add('header__mobile-navigation-item--active')
              trigger.classList.add('header__mobile-navigation-link--hidden')

              currentItems.forEach((item) => {
                gsap.set(item, { clearProps: 'transform,opacity', overwrite: true })
                item.classList.remove('header__mobile-navigation-item--hidden')
                if (!item.classList.contains('header__mobile-navigation-item--active'))
                  item.classList.add('header__mobile-navigation-item--hidden')
              })

              gsap.fromTo(
                activeItems,
                {
                  opacity: 0,
                  y: 123,
                },
                {
                  clearProps: 'transform,opacity',
                  duration: 1,
                  ease: 'power4.out',
                  opacity: 1,
                  overwrite: true,
                  stagger: 0.05,
                  y: 0,
                }
              )

              gsap.fromTo(
                el.mobileNavigation.bottomButtons,
                {
                  y: initialButtonsPosition - el.mobileNavigation.bottomButtons[0].getBoundingClientRect().top,
                },
                {
                  clearProps: 'transform',
                  duration: 0.75,
                  ease: 'power2.inOut',
                  stagger: -0.05,
                  y: 0,
                }
              )
            },
          })

          event.preventDefault()
        }
      })
    })

    el.mobileNavigation.backTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        const currentList = trigger.closest('.header__mobile-navigation-list')
        const currentItem = currentList.parentElement
        const currentItems = Array.from(currentList.children)
        const currentLink = currentList.previousElementSibling
        const parentItems = Array.from(currentItem.parentElement.children)
        const initialButtonsPosition = el.mobileNavigation.bottomButtons[0].getBoundingClientRect().top

        gsap.to(currentItems, {
          duration: 0.2,
          ease: 'power2.out',
          opacity: 0,
          overwrite: true,
          stagger: 0.05,
          y: -33,
          onComplete: () => {
            currentList.classList.remove('header__mobile-navigation-list--active')
            currentItem.classList.remove('header__mobile-navigation-item--active')
            currentLink.classList.remove('header__mobile-navigation-link--hidden')

            parentItems.forEach((item) => item.classList.remove('header__mobile-navigation-item--hidden'))

            gsap.fromTo(
              parentItems,
              {
                opacity: 0,
                y: 123,
              },
              {
                clearProps: 'transform,opacity',
                duration: 1,
                ease: 'power4.out',
                opacity: 1,
                overwrite: true,
                stagger: 0.05,
                y: 0,
              }
            )

            gsap.fromTo(
              el.mobileNavigation.bottomButtons,
              {
                y: initialButtonsPosition - el.mobileNavigation.bottomButtons[0].getBoundingClientRect().top,
              },
              {
                clearProps: 'transform',
                duration: 0.75,
                ease: 'power2.inOut',
                stagger: 0.05,
                y: 0,
              }
            )
          },
        })

        event.preventDefault()
      })
    })
  },
}
